import React, { useCallback, useEffect, useMemo } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import VirtualizedGroupedList from '@/components/VirtualizedGroupedList';

import FilterActions from '../FilterActions';
import FilterLabel from '../FilterLabel';
import Operators from '../Operators';
import SelectedItems from '../SelectedItems';
import { AsyncFilterProps, FilterOption } from '../types';
import { getMappedGroupToSelectedItems, isItemSelected } from '../utils';
import FilterEmptyState from './Search/FilterEmptyState';
import GroupContent from './GroupContent';
import ItemContent from './ItemContent';
import Loader from './Loader';
import AsyncFilterSearch from './Search';
import { filterBodyStyle } from './style';

function AsyncFilter(props: AsyncFilterProps) {
  const {
    asyncOptions,
    groups,
    actions,
    itemRenderer,
    isGroupSelectionEnabled,
    selectedFilters,
    showActionButtons,
    filterValue,
    label,
    isSingleSelect,
    operators,
    selectedOperator,
    emptyLabel,
    hideSelectedItems,
    getTooltipProps,
  } = props;
  const {
    addMultiple,
    removeMultiple,
    clearSelection,
    onApplyFilters,
    toggleOption,
    addSingle,
    setOperator,
  } = actions;
  const { search, fetching, fetchMore, classes, error } = asyncOptions;
  const { isMobile } = useScreen();

  useEffect(() => {
    return search?.onChange('');
  }, [search?.onChange]);

  const filteredPreviousSelectedFilters = useMemo(() => {
    const previousSelectedIds = filterValue.map((item) => item.value);
    return selectedFilters.filter((item) => previousSelectedIds.includes(item.value));
  }, [filterValue, selectedFilters]);

  const groupAndItemsMap = getMappedGroupToSelectedItems(groups, selectedFilters);

  const { groupCounts, filterItems } = useMemo(() => {
    const counts: number[] = [];
    const items: FilterOption[] = [];
    groups.forEach((group) => {
      const { options } = group;
      counts.push(options.length);
      items.push(...options);
    });
    return { groupCounts: counts, filterItems: items };
  }, [groups]);

  const renderGroupContent = useCallback(
    (index: number) => {
      const filterGroup = groups[index];
      const selectedItemsLength = groupAndItemsMap[filterGroup.id].length;

      return (
        <GroupContent
          key={filterGroup.id}
          addMultiple={addMultiple}
          filterGroup={filterGroup}
          isGroupSelectionEnabled={isGroupSelectionEnabled}
          isSingleSelect={isSingleSelect}
          removeMultiple={removeMultiple}
          selectedItemsLength={selectedItemsLength}
        />
      );
    },
    [
      groups,
      groupAndItemsMap,
      addMultiple,
      isGroupSelectionEnabled,
      isSingleSelect,
      removeMultiple,
    ],
  );

  const renderItemContent = useCallback(
    (index: number, groupIndex: number) => {
      const option = filterItems[index];
      const options = groups[groupIndex].options;

      if (!option) {
        return <Box />; // VirtualizedGroupedList returns extra index in some cases - https://github.com/petyosi/react-virtuoso/issues/319
      }

      const currentItemIndex = options.findIndex((item) => item.value === option.value);
      const isLast = options.length - 1 === currentItemIndex;
      const isSelected = isItemSelected(selectedFilters, option);

      return (
        <ItemContent
          key={option.value}
          getTooltipProps={getTooltipProps}
          isLast={isLast}
          isSelected={isSelected}
          isSingleSelect={isSingleSelect}
          itemRenderer={itemRenderer}
          option={option}
          onClick={isSingleSelect ? addSingle : toggleOption}
        />
      );
    },
    [
      addSingle,
      filterItems,
      groups,
      isSingleSelect,
      itemRenderer,
      selectedFilters,
      toggleOption,
      getTooltipProps,
    ],
  );

  const showSelectedItems =
    !Boolean(hideSelectedItems) && showActionButtons && filterValue.length && !search?.value;

  const renderContent = () => {
    if (error) {
      return null;
    }

    if (fetching || groups.length) {
      return (
        <VirtualizedGroupedList
          components={{
            Header: () => {
              return showSelectedItems ? (
                <SelectedItems
                  filterValue={filterValue}
                  itemRenderer={itemRenderer}
                  selectedOptions={filteredPreviousSelectedFilters}
                  toggleOption={toggleOption}
                />
              ) : null;
            },
            //todo can use ScrollSeekPlaceholder @prashant-15
            Footer: () => (fetching && !isMobile ? <Loader /> : null),
          }}
          endReached={fetchMore}
          groupContent={renderGroupContent}
          groupCounts={groupCounts}
          itemContent={renderItemContent}
        />
      );
    }

    return <FilterEmptyState emptyLabel={emptyLabel} search={search} />;
  };

  return (
    <Box className={cn(filterBodyStyle, 'flex flex-col overflow-x-hidden')}>
      {label ? <FilterLabel className="p-20 xs:px-24 xs:pt-32" label={label} /> : null}
      {operators?.length ? (
        <Operators
          operators={operators}
          selectedOperator={selectedOperator}
          setFilterOperator={setOperator}
        />
      ) : null}
      {search ? (
        <Box className="flex-shrink-0 px-8 pb-8 pt-12 xs:mb-0 xs:px-24 xs:pb-10">
          <AsyncFilterSearch search={search} />
        </Box>
      ) : null}
      <Box className={classes?.listContainer || cn('filter-items', 'pb-8 xs:pb-0')}>
        {renderContent()}
      </Box>
      {showActionButtons ? (
        <FilterActions onClear={clearSelection} onSave={onApplyFilters} />
      ) : null}
    </Box>
  );
}

export default React.memo(AsyncFilter);
